<script setup>
/* eslint-disable @intlify/vue-i18n/no-dynamic-keys */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import WorkflowCanvasCard from './WorkflowCanvasCard.vue';
import WorkflowFlowConnector from './WorkflowFlowConnector.vue';
import {
  formatDateTimeWindowSummary,
  getDateTimeWindowBranchTargetId,
} from './dateTimeWindow';
import {
  getWorkflowActionNodeTitle,
  getWorkflowActionStepDescriptionI18nKeys,
  getWorkflowActionStepVisual,
} from './workflowCanvasStepTheme';

const props = defineProps({
  stepId: { type: String, required: true },
  actions: { type: Array, required: true },
  automationActionTypes: { type: Array, required: true },
  addStepMenuItems: { type: Array, required: true },
  activePanel: { type: String, required: true },
  activeAddMenuId: { type: String, default: null },
  isReadOnly: { type: Boolean, default: false },
  branchTargetIds: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'update:activePanel',
  'update:activeAddMenuId',
  'insertActionAfter',
]);

const { t } = useI18n();

const branchTargetIdSet = computed(() => new Set(props.branchTargetIds));

const stepIndex = computed(() =>
  props.actions.findIndex(action => String(action.id) === String(props.stepId))
);

const step = computed(() => props.actions[stepIndex.value] || null);
const visual = computed(() =>
  step.value
    ? getWorkflowActionStepVisual(step.value.action_name)
    : getWorkflowActionStepVisual()
);

const stepDescriptionI18n = computed(() =>
  step.value
    ? getWorkflowActionStepDescriptionI18nKeys(step.value.action_name)
    : getWorkflowActionStepDescriptionI18nKeys()
);

const getActionParams = action =>
  Array.isArray(action.action_params)
    ? action.action_params[0] || {}
    : action.action_params || {};

const actionSummary = computed(() => {
  if (!step.value) return '';
  if (step.value.action_name === 'date_time_window') {
    return formatDateTimeWindowSummary(step.value.action_params);
  }

  if (step.value.action_name === 'add_private_note') {
    const params = step.value.action_params;
    return Array.isArray(params) ? params[0] || '' : params || '';
  }

  if (step.value.action_name === 'update_contact_tag') {
    const params = getActionParams(step.value);
    const action = t(
      `AUTOMATION.ACTIONS.${
        params.operation === 'remove' ? 'REMOVE_TAG' : 'ADD_TAG'
      }`
    );

    return t('AUTOMATION.FLOW.CONTACT_LABEL.ACTION', { action });
  }

  if (
    ['remove_assigned_agent', 'remove_assigned_team'].includes(
      step.value.action_name
    )
  ) {
    return t('AUTOMATION.FLOW.ASSIGNMENT.UNASSIGNED');
  }

  if (['assign_agent', 'assign_ai_agent'].includes(step.value.action_name)) {
    const assignee = getActionParams(step.value);
    const assigneeName =
      typeof assignee === 'object'
        ? assignee.available_name || assignee.name
        : null;

    return assigneeName
      ? t('AUTOMATION.FLOW.ASSIGNMENT.AGENT', { name: assigneeName })
      : t('AUTOMATION.FLOW.ASSIGNMENT.UNASSIGNED');
  }

  if (step.value.action_name === 'assign_team') {
    const team = getActionParams(step.value).team_id;
    const teamName = typeof team === 'object' ? team.name : null;

    return teamName
      ? t('AUTOMATION.FLOW.ASSIGNMENT.TEAM', { name: teamName })
      : t('AUTOMATION.FLOW.ASSIGNMENT.UNASSIGNED');
  }

  const type = props.automationActionTypes.find(
    action => action.key === step.value.action_name
  );
  return type?.label || step.value.action_name;
});

const actionShortDescription = computed(() => {
  if (step.value?.action_name === 'add_private_note') return '';

  if (step.value?.action_name === 'update_contact_tag') {
    return '';
  }

  if (
    [
      'assign_agent',
      'assign_ai_agent',
      'assign_team',
      'remove_assigned_agent',
      'remove_assigned_team',
    ].includes(step.value?.action_name)
  ) {
    return '';
  }

  return t(stepDescriptionI18n.value.short);
});

const actionLabelPills = computed(() => {
  if (step.value?.action_name !== 'update_contact_tag') return [];

  const params = getActionParams(step.value);

  return (params.tags || [])
    .map(label => {
      if (typeof label === 'string') {
        return { title: label };
      }

      return {
        title: label.name || label.title,
        color: label.color,
        emoji: label.emoji,
      };
    })
    .filter(label => label.title);
});

const actionNodeTitle = computed(() => {
  if (!step.value) return '';
  return getWorkflowActionNodeTitle(
    step.value.action_name,
    props.actions,
    stepIndex.value,
    t
  );
});

const childStepId = computed(() => {
  if (!step.value || step.value.action_name === 'date_time_window') {
    return null;
  }

  if (step.value.next_step_id) {
    return String(step.value.next_step_id);
  }

  if (branchTargetIdSet.value.has(String(step.value.id))) {
    return null;
  }

  const nextStep = props.actions[stepIndex.value + 1];
  return nextStep?.id ? String(nextStep.id) : null;
});

const successStepId = computed(() =>
  step.value?.action_name === 'date_time_window'
    ? getDateTimeWindowBranchTargetId(step.value.action_params, 'success')
    : null
);

const failureStepId = computed(() =>
  step.value?.action_name === 'date_time_window'
    ? getDateTimeWindowBranchTargetId(step.value.action_params, 'failure')
    : null
);

const isActive = computed(
  () => props.activePanel === `action-${stepIndex.value}`
);

const select = () => {
  emit('update:activePanel', `action-${stepIndex.value}`);
};

const onAddStep = (actionName, options = {}) => {
  emit('insertActionAfter', stepIndex.value, actionName, options);
};

const updateActiveAddMenuId = value => {
  emit('update:activeAddMenuId', value);
};

const activeAddMenuIdProxy = computed({
  get() {
    return props.activeAddMenuId;
  },
  set(value) {
    updateActiveAddMenuId(value);
  },
});
</script>

<template>
  <div class="flex min-w-[13.125rem] flex-col items-center">
    <div class="relative">
      <WorkflowCanvasCard
        :title="actionNodeTitle"
        :description="actionSummary"
        :sub-description="actionShortDescription"
        :label-pills="actionLabelPills"
        :icon="visual.hugeIcon"
        :icon-class="visual.iconClass"
        :is-active="isActive"
        :active-card-class="visual.cardBorderClass"
        :active-ring-class="visual.ringActiveClass"
        @click="select"
      />
    </div>

    <template v-if="step.action_name === 'date_time_window'">
      <div
        class="inline-grid grid-cols-[max-content_6rem_max-content] justify-items-center"
      >
        <div class="col-span-3 flex w-full justify-center">
          <div class="h-3 w-0.5" :class="visual.lineClass" aria-hidden="true" />
        </div>
        <div
          class="h-0.5 w-1/2 justify-self-end"
          :class="visual.lineClass"
          aria-hidden="true"
        />
        <div
          class="h-0.5 w-full"
          :class="visual.lineClass"
          aria-hidden="true"
        />
        <div
          class="h-0.5 w-1/2 justify-self-start"
          :class="visual.lineClass"
          aria-hidden="true"
        />
        <div class="h-5 w-0.5" :class="visual.lineClass" aria-hidden="true" />
        <div />
        <div class="h-5 w-0.5" :class="visual.lineClass" aria-hidden="true" />

        <div class="flex flex-col items-center pt-1">
          <span
            class="mb-2 rounded-md bg-n-green-4 px-2 py-0.5 text-xs font-medium text-n-green-11 dark:bg-n-green-4/30"
          >
            {{ $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.BRANCH_SUCCESS') }}
          </span>
          <div class="h-4 w-0.5" :class="visual.lineClass" aria-hidden="true" />
          <WorkflowFlowConnector
            v-model:active-add-menu-id="activeAddMenuIdProxy"
            :connector-id="`date-time-success-root-${step.id}`"
            :line-class="visual.lineClass"
            :plus-button-class="visual.plusButtonClass"
            :items="addStepMenuItems"
            :is-read-only="isReadOnly"
            @select="
              actionName => onAddStep(actionName, { branchKey: 'success' })
            "
          />
          <WorkflowStepTree
            v-if="successStepId"
            :step-id="String(successStepId)"
            :actions="actions"
            :automation-action-types="automationActionTypes"
            :add-step-menu-items="addStepMenuItems"
            :active-panel="activePanel"
            :active-add-menu-id="activeAddMenuId"
            :is-read-only="isReadOnly"
            :branch-target-ids="branchTargetIds"
            @update:active-panel="$emit('update:activePanel', $event)"
            @update:active-add-menu-id="updateActiveAddMenuId"
            @insert-action-after="
              (...args) => $emit('insertActionAfter', ...args)
            "
          />
        </div>

        <div />

        <div class="flex flex-col items-center pt-1">
          <span
            class="mb-2 rounded-md bg-n-ruby-4 px-2 py-0.5 text-xs font-medium text-n-ruby-11 dark:bg-n-ruby-4/30"
          >
            {{ $t('AUTOMATION.FLOW.DATE_TIME_WINDOW.BRANCH_FAILURE') }}
          </span>
          <div class="h-4 w-0.5" :class="visual.lineClass" aria-hidden="true" />
          <WorkflowFlowConnector
            v-model:active-add-menu-id="activeAddMenuIdProxy"
            :connector-id="`date-time-failure-root-${step.id}`"
            :line-class="visual.lineClass"
            :plus-button-class="visual.plusButtonClass"
            :items="addStepMenuItems"
            :is-read-only="isReadOnly"
            @select="
              actionName => onAddStep(actionName, { branchKey: 'failure' })
            "
          />
          <WorkflowStepTree
            v-if="failureStepId"
            :step-id="String(failureStepId)"
            :actions="actions"
            :automation-action-types="automationActionTypes"
            :add-step-menu-items="addStepMenuItems"
            :active-panel="activePanel"
            :active-add-menu-id="activeAddMenuId"
            :is-read-only="isReadOnly"
            :branch-target-ids="branchTargetIds"
            @update:active-panel="$emit('update:activePanel', $event)"
            @update:active-add-menu-id="updateActiveAddMenuId"
            @insert-action-after="
              (...args) => $emit('insertActionAfter', ...args)
            "
          />
        </div>
      </div>
    </template>

    <template v-else>
      <WorkflowFlowConnector
        v-model:active-add-menu-id="activeAddMenuIdProxy"
        :connector-id="`after-action-${step.id}`"
        :line-class="visual.lineClass"
        :plus-button-class="visual.plusButtonClass"
        :items="addStepMenuItems"
        :is-read-only="isReadOnly"
        @select="actionName => onAddStep(actionName)"
      />

      <WorkflowStepTree
        v-if="childStepId"
        :step-id="String(childStepId)"
        :actions="actions"
        :automation-action-types="automationActionTypes"
        :add-step-menu-items="addStepMenuItems"
        :active-panel="activePanel"
        :active-add-menu-id="activeAddMenuId"
        :is-read-only="isReadOnly"
        :branch-target-ids="branchTargetIds"
        @update:active-panel="$emit('update:activePanel', $event)"
        @update:active-add-menu-id="updateActiveAddMenuId"
        @insert-action-after="(...args) => $emit('insertActionAfter', ...args)"
      />
    </template>
  </div>
</template>
